import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void; // PHASE 4: Allow parent to provide reset callback
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Silent logging - errors are suppressed and logged only
    console.error('[ErrorBoundary] Error caught and suppressed:', error.message);
    console.debug('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  // PHASE 4: Reset error state
  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    // Call parent's onReset callback if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      // If fallback provided, use it. Otherwise, render children anyway (graceful degradation)
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      
      // Silent mode: render null (no error UI shown)
      return null;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;